import { useEffect, useState } from 'react';
import {
  Button,
  Label,
  Paragraph,
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/atoms';
import { ArrowLeftIcon } from 'lucide-react';
import Facebook from '@/assets/svg/facebook.svg?react';
import Instagram from '@/assets/svg/instagram.svg?react';
import Whatsapp from '@/assets/svg/whatsapp.svg?react';
import CheckBadge from '@/assets/svg/check-badge.svg?react';
import type { SocialChannel } from '@/types';

import type { ChannelType } from '@/types';

const WHATSAPP_CONNECT_INFO =
  'Connect the same WhatsApp Business number you provided during onboarding. Make sure it’s an active business number that you currently use to communicate with your customers.';

interface ConnectModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  onConnect: (channel: ChannelType) => void;
  isConnecting?: boolean;
  defaultChannel?: ChannelType;
}

export const ConnectModal = ({
  open,
  setOpen,
  onConnect,
  isConnecting = false,
  defaultChannel = 'instagram',
}: ConnectModalProps) => {
  const [selectedChannel, setSelectedChannel] =
    useState<ChannelType>(defaultChannel);

  useEffect(() => {
    if (open) {
      setSelectedChannel(defaultChannel);
    }
  }, [open, defaultChannel]);

  const handleClose = (open: boolean) => {
    setOpen(open);
    if (!open) {
      setSelectedChannel(defaultChannel);
    }
  };

  const socialChannels: SocialChannel[] = [
    {
      id: 'whatsapp',
      name: 'Whatsapp',
      icon: Whatsapp,
      color: 'bg-green-500',
    },
    {
      id: 'instagram',
      name: 'Instagram',
      icon: Instagram,
      color: 'bg-pink-500',
    },
    {
      id: 'facebook',
      name: 'Facebook',
      icon: Facebook,
      color: 'bg-blue-500',
    },
  ];

  const isWhatsapp = selectedChannel === 'whatsapp';

  const handleConnect = () => {
    onConnect(selectedChannel);
  };

  return (
    <Sheet open={open} onOpenChange={handleClose}>
      <SheetContent className="max-sm:w-screen overflow-y-auto pb-4 px-4 lg:px-10 flex flex-col">
        <SheetHeader className="flex px-0 flex-row items-center justify-between">
          <Button
            className="!justify-left"
            variant="ghost"
            size="icon"
            onClick={() => handleClose(false)}
          >
            <ArrowLeftIcon className="w-4 h-4" />
          </Button>
        </SheetHeader>

        <div className="flex flex-col mt-24 flex-1">
          <SheetTitle className="lg:text-4xl max-w-sm leading-tight font-bold text-primary text-left">
            Connect your sales account here
          </SheetTitle>

          <div className="flex flex-col mt-8 gap-4 w-full max-w-md">
            <Label className="text-sm font-medium">Sales Channel*</Label>
            <div className="grid grid-cols-3 gap-3">
              {socialChannels.map((channel) => {
                const IconComponent = channel.icon;
                const isSelected = selectedChannel === channel.id;
                return (
                  <Button
                    key={channel.id}
                    variant={isSelected ? 'default' : 'outline'}
                    className={`flex items-center relative gap-2 font-bold text-lg hover:bg-gray-100 hover:text-gray-500 p-4 h-auto ${
                      isSelected
                        ? 'bg-green-50 hover:bg-green-50 hover:text-green-600 text-green-600'
                        : 'bg-gray-100 text-gray-500 border-none'
                    }`}
                    onClick={() => {
                      setSelectedChannel(channel.id as ChannelType);
                    }}
                  >
                    <div className="flex items-center bg-white rounded-full p-1">
                      <IconComponent className="w-5 h-5" />
                    </div>
                    <span className="text-sm font-medium">{channel.name}</span>
                    {isSelected && (
                      <CheckBadge className="w-6 h-6 absolute -right-2 top-0" />
                    )}
                  </Button>
                );
              })}
            </div>
          </div>

          <div className="mt-10 max-w-md flex flex-col gap-2">
            {isWhatsapp ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    className="w-full px-8 py-3 rounded-md text-base"
                    onClick={handleConnect}
                    disabled={isConnecting}
                  >
                    {isConnecting ? 'Connecting...' : 'Connect to continue'}
                  </Button>
                </TooltipTrigger>
                <TooltipContent
                  sideOffset={8}
                  className="w-auto max-w-[min(18rem,calc(100vw-2.5rem))] whitespace-normal text-left leading-relaxed shadow-md"
                >
                  {WHATSAPP_CONNECT_INFO}
                </TooltipContent>
              </Tooltip>
            ) : (
              <Button
                className="w-full px-8 py-3 rounded-md text-base"
                onClick={handleConnect}
                disabled={isConnecting}
              >
                {isConnecting ? 'Connecting...' : 'Connect to continue'}
              </Button>
            )}
          </div>
        </div>

        <div className="mt-auto pt-4 text-center">
          <Paragraph className="text-xs text-center tracking-widest text-muted-foreground">
            POWERED BY GOTHRIVE TECHNOLOGIES
          </Paragraph>
        </div>
      </SheetContent>
    </Sheet>
  );
};
